import { Texture } from "three";

/**
 * Lato (in metri di superficie reale) coperto da un campione di finitura.
 * Le tinte RAL sono immagini a tinta unita e non ne risentono; conta per le finiture
 * a texture - woodstyle in testa - dove un valore troppo piccolo moltiplica le
 * ripetizioni e fa comparire la griglia di quadrati, uno troppo grande stira la venatura.
 */
export const TEXTURE_TILE_M = 1.6;

// I cloni condividono la stessa Source di three: sulla GPU resta una sola texture,
// cambia solo la matrice UV. La cache evita di ricrearli a ogni frame.
const cache = new Map<string, Texture>();
const CACHE_MAX = 64;

/**
 * Texture mappata sulla superficie in coordinate reali invece che 0..1.
 *
 * Le UV delle geometrie dei pannelli sono normalizzate sulla singola superficie: usate
 * cosi' ogni pannello ripartirebbe dallo stesso punto del campione e le venature si
 * allineerebbero riga per riga (effetto "cubi"). Passando anche la posizione della
 * superficie sulla porta il disegno prosegue senza interruzioni da un pannello all'altro.
 *
 * @param larghezza/altezza dimensioni reali della superficie, in metri
 * @param offsetX/offsetY   posizione dello spigolo basso-sinistro della superficie
 *                          rispetto allo spigolo basso-sinistro della porta, in metri
 */
export function texturaTile(
  txt: Texture | undefined,
  larghezza: number,
  altezza: number,
  offsetX: number = 0,
  offsetY: number = 0,
): Texture | undefined {
  if (!txt) return undefined;

  const rx = Math.max(larghezza, 0) / TEXTURE_TILE_M;
  const ry = Math.max(altezza, 0) / TEXTURE_TILE_M;
  if (rx <= 0 || ry <= 0) return txt;

  const ox = offsetX / TEXTURE_TILE_M;
  const oy = offsetY / TEXTURE_TILE_M;

  const key = `${txt.uuid}|${rx.toFixed(3)}|${ry.toFixed(3)}|${ox.toFixed(3)}|${oy.toFixed(3)}`;
  const cached = cache.get(key);
  if (cached) return cached;

  // clone() eredita wrapping, filtri e colorSpace dalla texture originale e marca
  // gia' needsUpdate: qui bastano ripetizione e scostamento.
  const clone = txt.clone();
  clone.repeat.set(rx, ry);
  clone.offset.set(ox, oy);

  if (cache.size >= CACHE_MAX) {
    const oldest = cache.keys().next();
    if (!oldest.done) {
      cache.get(oldest.value)?.dispose();
      cache.delete(oldest.value);
    }
  }
  cache.set(key, clone);

  return clone;
}
